/**
 * Shared video element for iOS.
 *
 * iOS requires video.play() to originate from a valid user gesture.
 * Once a <video> element has been "blessed" (played via user tap), subsequent
 * play() calls on that SAME element succeed without a new gesture — even after
 * changing its src. This is the technique YouTube Shorts web uses: one blessed
 * video element whose src is swapped as the user scrolls.
 *
 * Chrome iOS (WKWebView) may reset this permission when src changes.
 * To handle this, `rebless()` is called on every touchend so the element
 * is re-authorized inside a valid gesture handler before the next play().
 *
 * IMPORTANT: The video element is a **global singleton** that persists across
 * route navigations. This allows the "blessing" from a click on the home
 * carousel to carry over to the /shorts page — the element is already
 * authorized to play when the feed mounts.
 */

export interface IOSSharedVideoAPI {
	videoElement: Ref<HTMLVideoElement | null>;
	rebless: () => void;
}

const IOS_SHARED_VIDEO_KEY = Symbol(
	'ios-shared-video'
) as InjectionKey<IOSSharedVideoAPI>;

// All iOS browsers use WebKit under the hood, so the autoplay restriction
// applies to Chrome, Firefox, Edge, etc. — not just Safari.
export const isIOSDevice =
	import.meta.client && /iPad|iPhone|iPod/.test(navigator.userAgent);

function createSharedVideoElement(): HTMLVideoElement {
	const video = document.createElement('video');
	video.playsInline = true;
	video.muted = true;
	video.preload = 'auto';
	video.style.cssText =
		'width:100%;height:100%;object-fit:cover;position:absolute;inset:0';
	return video;
}

// Global singleton — survives route changes so the blessing persists.
let globalVideoElement: HTMLVideoElement | null = null;
// Tracks whether a user gesture has blessed the shared video element.
// Starts as false on every fresh page load. Set to true by blessSharedVideoElement()
// and rebless() which are only called from user gesture handlers (click/touchend).
let globalVideoIsBlessed = false;

function getOrCreateSharedVideoElement(): HTMLVideoElement {
	if (!globalVideoElement) {
		globalVideoElement = createSharedVideoElement();
	}
	return globalVideoElement;
}

/**
 * Bless the global shared video element. Must be called synchronously inside
 * a user gesture handler (click, touchend). This is meant to be called from
 * entry points (e.g. home carousel card click) BEFORE navigating to /shorts,
 * so the element already has play permission when the feed mounts.
 *
 * Calling play() without a src is enough — WebKit marks the element as
 * user-activated. The promise rejects but the permission is recorded.
 */
export function blessSharedVideoElement(): void {
	if (!isIOSDevice) return;
	globalVideoIsBlessed = true;
	const video = getOrCreateSharedVideoElement();
	video.muted = false;
	video.play().catch(() => {});
}

/**
 * Returns true if a user gesture has blessed the shared video element,
 * meaning play() with or without audio should work. False on a fresh
 * server render where no user interaction has occurred yet.
 */
export function isSharedVideoElementBlessed(): boolean {
	return globalVideoIsBlessed;
}

/**
 * Clear the global shared video element. Call this during cleanup to allow
 * garbage collection when the app is being destroyed or the user navigates away.
 */
export function clearSharedVideoElement(): void {
	if (globalVideoElement) {
		try {
			globalVideoElement.pause();
			globalVideoElement.removeAttribute('src');
			globalVideoElement.load();
			// Remove from DOM if present
			globalVideoElement.parentElement?.removeChild(globalVideoElement);
		} catch {
			// Ignore cleanup errors
		}
		globalVideoElement = null;
	}
	globalVideoIsBlessed = false;
}

export function provideIOSSharedVideo(): IOSSharedVideoAPI {
	const videoElement = ref<HTMLVideoElement | null>(null);

	if (isIOSDevice) {
		videoElement.value = getOrCreateSharedVideoElement();
	}

	function rebless() {
		const video = videoElement.value;
		if (!video || !video.src) return;
		globalVideoIsBlessed = true;
		video.play().catch(() => {});
	}

	onBeforeUnmount(() => {
		// Don't destroy the global singleton here - it persists across route changes.
		// Only cleanup this component's reference.
		videoElement.value = null;
	});

	const api: IOSSharedVideoAPI = { videoElement, rebless };
	provide(IOS_SHARED_VIDEO_KEY, api);
	return api;
}

export function useIOSSharedVideo(): IOSSharedVideoAPI | null {
	if (!isIOSDevice) return null;
	return inject(IOS_SHARED_VIDEO_KEY, null);
}
